<template>
    <div class="flex justify-center w-full">
        <div class="flex flex-col w-3/5">
            <!-- Contents -->
            <h2>Create Multi-Point Calibration Profile</h2>

            <div    class="flex flex-row justify-between py-1">
                <!-- Profile Name -->
                <div   class="w-2/5 2xl:w-1/3 flex flex-col">
                    <floating-input     id="profileName"
                                        v-model:value="name"
                                        label="Profile Name"
                                        placeholder="Enter Profile Name">
                    </floating-input>

                </div>

                <div    class="w-2/5 2xl:w-1/3 ">
                    <!-- Reference Document Name -->
                    <div    class="">
                        <floating-input    id="reference_document_name"
                                            v-model:value="referenceDocumentName"
                                            label="Reference Document Name"
                                            placeholder="Enter Reference Document Name">
                        </floating-input>
                    </div>

                    <!-- Select Probe Type Index -->
                    <select2    v-model:value="probeTypeIndexes"
                                :options="probeTypes"
                                :multiple="true"
                                label="Probe Type"
                                placeholder="Select Probe Type">
                    </select2>
                </div>
            </div>

            <!-- Rules -->
            <div    class="py-1">
                <tailwind-table     :data="rules"
                                    :exportable="false"
                                    :headers="headers">
                    <!-- Input for Target Value -->
                    <template    #col-1="props">
                        <div    class="flex flex-col">
                            <floating-input     :id="'targetValue_' + props.rowIndex"
                                                v-model:value="props.row.targetValue"
                                                label="Target Value"
                                                placeholder="Enter Target Value">
                            </floating-input>

                            <span   v-if="hasError(props.rowIndex, 'targetValue')"
                                    class="text-red-500">{{ errors[props.rowIndex]['targetValue'] }}</span>
                        </div>
                    </template>

                    <!-- Input for Tolerance -->
                    <template    #col-2="props">
                        <div    class="flex flex-col">
                            <floating-input     :id="'tolerance_' + props.rowIndex"
                                                v-model:value="props.row.tolerance"
                                                label="Tolerance"
                                                placeholder="Enter Tolerance">
                            </floating-input>

                            <span   v-if="hasError(props.rowIndex, 'tolerance')"
                                    class="text-red-500">{{ errors[props.rowIndex]['tolerance'] }}</span>
                        </div>
                    </template>

                    <!-- Add Buttons for adding and removing rules -->
                    <template   #col-4="props">
                        <div class="flex flex-row justify-center items-center">
                            <!-- Add Rule -->
                            <!-- Only show the add row on the last row -->
                            <button     v-if="props.rowIndex === rules.length - 1"
                                        class="inline-flex justify-center items-center w-8 h-8 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                                font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                                focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                                    @click="addBlankRule">
                                <span class="lnr icon-plus"></span>
                            </button>

                            <!-- Remove Rule -->
                            <button     class="inline-flex justify-center items-center w-8 h-8 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                                font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                                focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                                    @click="removeRule(props.rowIndex)">
                                <span class="lnr icon-minus"></span>
                            </button>
                        </div>
                    </template>
                </tailwind-table>
            </div>

            <!-- Save Button -->
            <div    class="pt-2 flex flex-col w-full items-center">
                <button v-if="savingMultiPointCalibrationProfile"
                        class="inline-flex justify-center items-center w-full ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <span class="lnr icon-loading animate-spin mr-3"></span>
                    Creating Profile, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-full ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                        @click="createNewProfile">
                    Create Profile
                </button>

            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import ButtonBluePrimary from "../elements/buttons/ButtonBluePrimary.vue";
import FloatingInput from "../elements/FloatingInput.vue";
import TailwindTable from "../elements/TailwindTable.vue";
import Select2 from "../elements/select2.vue";

export default {
    name: "MultiPointCalibrationProfileCreate",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {Select2, TailwindTable, FloatingInput, ButtonBluePrimary},
    emits: ['profile-created'],
    props: {
        // Probe types for the dropdown
        probeTypes: {
            type: [Array, Object],
            default: () => []
        },
    },
    data() {
        return {
            // Data Properties
            name: '',
            probeTypeIndexes: null,
            referenceDocumentName: '',

            // Target values to test & additional information
            headers: [
                {
                    name: 'Target Value',
                    sortBy: 'targetValue',
                    key: 'targetValue',
                    show: 'sm',
                },
                {
                    name: 'Tolerance',
                    sortBy: 'tolerance',
                    key: 'tolerance',
                    show: 'sm',
                },
                {
                    name: '',
                    sortBy: '',
                    key: '',
                    show: 'sm',
                },
                {
                    name: 'Add/Remove',
                    sortBy: '',
                    key: '',
                    show: 'sm',
                },
            ],
            rules: [
                {
                    targetValue: '',
                    tolerance: '',
                },
            ],

            // Saving
            savingMultiPointCalibrationProfile: false,

            // Errors
            errors: {},
        }
    },
    created() {

    },
    mounted() {

    },
    methods: {
        // Methods
        createNewProfile() {
            // Prep Parameters for parameter creation
            let params = {
                name: this.name,
                referenceDocumentName: this.referenceDocumentName,
                probeTypeIndexes: this.probeTypeIndexes,
            }
            let rules = _.map(this.rules, rule => {
                return {
                    targetValue: rule.targetValue,
                    tolerance: rule.tolerance,
                }
            })
            params.rules = rules

            this.savingMultiPointCalibrationProfile = true;
            axios.post(this.company.buildApiInternalRoute('multi-point-calibration-profiles'), params
            ).then(response => {
                let data = response.data
                console.log("Profile Created")
                this.errors = []
                this.$emit('profile-created')
            }).catch(error => {
                // Handle Error
                let errorData = error.response.data
                this.errors = errorData.errors
            }).then(() => {
                this.savingMultiPointCalibrationProfile = false;
            })
        },
        addBlankRule() {
            this.rules.push({
                targetValue: '',
                tolerance: '',
            })
        },
        removeRule(index) {
            // Ensure that the index is valid
            if (index < 0 || index >= this.rules.length) {
                return
            }

            // Remove the selected rule
            this.rules.splice(index, 1)

            // If the last rule is removed, add a new (blank) rule
            if (this.rules.length === 0) {
                this.addBlankRule()
            }
        },
        hasError(index, key) {
            return ! _.isNil(this.errors) && _.has(this.errors, index) && ! _.isNil(this.errors[index][key])
        },
    },
    computed: {},
    watch: {},
}
</script>

<style scoped>

</style>